import { useState, useRef } from 'react';
import { useDialog } from '../useDialog';
import { Broll, Caption, CaptionStyle } from '@t2c/ai-video-editor';
import { CaptionTimeRange } from '../../@types';

interface CaptionEditorProps {
  captionStyle: CaptionStyle;
  captions: Caption[];
  setCaptions: (value: Caption[]) => void;
  brolls: Broll[];
  setBrolls: (value: Broll[]) => void;
}

export const useCaptionEditor = ({
  captionStyle,
  captions,
  setCaptions,
  brolls,
  setBrolls,
}: CaptionEditorProps) => {
  const [currentWord, setCurrentWord] = useState<{
    mainIndex: number;
    subIndex: number;
  } | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [captionTimeRange, setCaptionTimeRange] = useState<CaptionTimeRange>({
    startTime: 0,
    endTime: 0,
  });

  const [selectedCaptionIndexForSound, setSelectedCaptionIndexForSound] = useState<number | null>(
    null
  );

  const editMenuRef = useRef<HTMLDivElement>(null);

  const {
    isDialogOpen: isPositionYDialogOpen,
    openDialog: openPositionYDialog,
    closeDialog: closePositionYDialog,
  } = useDialog();

  const {
    isDialogOpen: isTimestampDialogOpen,
    openDialog: openTimestampDialog,
    closeDialog: closeTimestampDialog,
  } = useDialog();

  const {
    isDialogOpen: isCaptionThemesDialogOpen,
    openDialog: openCaptionThemesDialog,
    closeDialog: closeCaptionThemesDialog,
  } = useDialog();

  const {
    isDialogOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog();

  const {
    isDialogOpen: isOpenSoundEffectModal,
    openDialog: openSoundEfffectModal,
    closeDialog: closeSoundEffectModal,
  } = useDialog();

  const handleOpenTimeStampDialog = ({ startTime, endTime, mainIndex }) => {
    setCaptionTimeRange({ startTime, endTime });
    setCurrentWord({ mainIndex, subIndex: 0 });
    openTimestampDialog();
  };

  const handleOpenWordEditMenu = (event: any, mainIndex: number, subIndex: number) => {
    setAnchorEl(event.currentTarget);
    setCurrentWord({ mainIndex, subIndex });
  };

  const handleCloseWordEditMenu = () => {
    setAnchorEl(null);
    setCurrentWord(null);
  };

  const updateBrolls = (startTime: number, endTime: number, captions: Caption[]) => {
    const index = brolls.findIndex((res) => res.startTime <= startTime && res.endTime >= endTime);
    let newBrollText = '';
    captions.map((res) => {
      res.words.map((word) => {
        if (word.startTime >= brolls[index].startTime && word.endTime <= brolls[index].endTime)
          newBrollText += ` ${word.word}`;
      });
    });
    brolls[index].text = newBrollText;
    setBrolls([...brolls]);
  };

  const handleSubtitleWordChange = (
    mainIndex: number,
    subIndex: number,
    value: string,
    startTime: number,
    endTime: number
  ) => {
    const updatedSubtitles = [...captions];
    const targetSubtitle = updatedSubtitles[mainIndex];
    if (targetSubtitle) {
      targetSubtitle.words[subIndex].word = value;
    }
    setCaptions(updatedSubtitles);
    updateBrolls(startTime, endTime, updatedSubtitles);
  };

  const handleHighlightWord = (id?: number) => {
    if (currentWord) {
      const { mainIndex, subIndex } = currentWord;
      const updatedSubtitles = [...captions];
      const currentSubtitle = updatedSubtitles[mainIndex];

      currentSubtitle.words[subIndex] = {
        ...currentSubtitle.words[subIndex],
        highlightColorId: String(id) || '',
      };

      setCaptions(updatedSubtitles);
      handleCloseWordEditMenu();
    }
  };

  const handleAddWord = () => {
    if (currentWord) {
      const { mainIndex, subIndex } = currentWord;
      const currentSubtitle = captions[mainIndex];
      const word = captions[mainIndex].words[subIndex];

      const newWord = {
        word: 'New word',
        // startTime: currentSubtitle.endTime,
        // endTime: currentSubtitle.endTime + 1,
        startTime: word.endTime,
        endTime: word.endTime,
      };

      const updatedWordsArray = [
        ...currentSubtitle.words.slice(0, subIndex + 1),
        newWord,
        ...currentSubtitle.words.slice(subIndex + 1),
      ];

      const updatedSubtitle = {
        ...currentSubtitle,
        words: updatedWordsArray,
        text: updatedWordsArray.map((w) => w.word).join(' '),
      };

      const updatedSubtitles = [
        ...captions.slice(0, mainIndex),
        updatedSubtitle,
        ...captions.slice(mainIndex + 1),
      ];
      setCaptions(updatedSubtitles);
      updateBrolls(
        currentSubtitle.words[subIndex].endTime,
        currentSubtitle.words[subIndex].endTime,
        updatedSubtitles
      );
      handleCloseWordEditMenu();
    }
  };

  const handleRemoveWord = () => {
    if (currentWord) {
      const { mainIndex, subIndex } = currentWord;
      const currentSubtitle = captions[mainIndex];

      if (currentSubtitle.words.length <= 1) {
        const updatedSubtitles = [
          ...captions.slice(0, mainIndex),
          ...captions.slice(mainIndex + 1),
        ];

        setCaptions(updatedSubtitles);
        updateBrolls(currentSubtitle.startTime, currentSubtitle.endTime, updatedSubtitles);
        handleCloseWordEditMenu();
        return; // Exit the function after removing the subtitle
      }

      const updatedWordsArray = [
        ...currentSubtitle.words.slice(0, subIndex),
        ...currentSubtitle.words.slice(subIndex + 1),
      ];

      const updatedSubtitle = {
        ...currentSubtitle,
        words: updatedWordsArray,
        text: updatedWordsArray.map((w) => w.word).join(' '),
      };

      const updatedSubtitles = [
        ...captions.slice(0, mainIndex),
        updatedSubtitle,
        ...captions.slice(mainIndex + 1),
      ];

      setCaptions(updatedSubtitles);
      updateBrolls(currentSubtitle.startTime, currentSubtitle.endTime, updatedSubtitles);
      handleCloseWordEditMenu();
    }
  };

  const handleOnUpdateCaptionTimeRange = ({ startTime, endTime }: CaptionTimeRange) => {
    if (currentWord) {
      const { mainIndex } = currentWord;
      const updatedSubtitles = [...captions];
      const currentSubtitle = updatedSubtitles[mainIndex];

      currentSubtitle.startTime = startTime;
      currentSubtitle.endTime = endTime;
      setCaptions(updatedSubtitles);
    }
    closeTimestampDialog();
  };

  const handleSoundSelect = (soundEffectUrl: string) => {
    captions[selectedCaptionIndexForSound].soundEffectUrl = soundEffectUrl;
    if (!captions[selectedCaptionIndexForSound].soundEffectVolume) {
      captions[selectedCaptionIndexForSound].soundEffectVolume = 100;
    }
    setCaptions([...captions]);

    closeSoundEffectModal();
  };

  const handleVolumeChange = (e, val: number) => {
    captions[selectedCaptionIndexForSound].soundEffectVolume = val;
    setCaptions([...captions]);
  };

  const handleRemoveSound = () => {
    captions[selectedCaptionIndexForSound].soundEffectUrl = '';
    setCaptions([...captions]);

    closeSoundEffectModal();
  };
  return {
    anchorEl,
    isPositionYDialogOpen,
    openPositionYDialog,
    closePositionYDialog,
    isTimestampDialogOpen,
    closeTimestampDialog,
    isCaptionThemesDialogOpen,
    openCaptionThemesDialog,
    closeCaptionThemesDialog,
    isDeleteDialogOpen,
    openDeleteDialog,
    closeDeleteDialog,
    handleOpenWordEditMenu,
    handleCloseWordEditMenu,
    handleSubtitleWordChange,
    editMenuRef,
    handleHighlightWord,
    handleAddWord,
    handleRemoveWord,
    captionTimeRange,
    handleOpenTimeStampDialog,
    handleOnUpdateCaptionTimeRange,
    currentWord,
    isOpenSoundEffectModal,
    openSoundEfffectModal,
    closeSoundEffectModal,
    handleSoundSelect,
    handleRemoveSound,
    handleVolumeChange,
    selectedCaptionIndexForSound,
    setSelectedCaptionIndexForSound,
  };
};
